import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Testimonial } from '../../../_models/testimonial';
import { TestimonialService } from '../../../_services/testimonial.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-testimonial-form',
    templateUrl: './testimonial-form.component.html',
    styleUrls: ['./testimonial-form.component.css'],
    standalone: false
})
export class TestimonialFormComponent implements OnInit {
    title = 'Add Testimonial';
    testimonialId: string | null = null;
    loading = true;
    submitting = false;
    form: UntypedFormGroup;
    testimonial: Testimonial | null = null;
    photo: Blob | null = null;
    previewUrl: string | ArrayBuffer | null = null;
    existingPhotoUrl = '';

    constructor(
        private titleService: Title,
        private router: Router,
        private fb: UntypedFormBuilder,
        private activatedRoute: ActivatedRoute,
        private testimonialService: TestimonialService,
        private toastr: ToastrService
    ) {
        this.testimonialId = this.activatedRoute.snapshot.paramMap.get('id');
        if (!this.testimonialId) {
            this.titleService.setTitle(this.title);
        } else {
            this.title = 'Edit Testimonial';
        }
    }

    ngOnInit() {
        this.buildForm();
        if (this.testimonialId) {
            this.loadTestimonial();
        } else {
            this.loading = false;
        }
    }

    loadTestimonial() {
        this.testimonialService.loadOne(this.testimonialId!).subscribe({
            next: (response: any) => {
                const data = response.data || response;
                this.testimonial = data;
                this.existingPhotoUrl = this.getFullPhotoUrl(data.profilePhotoPath);
                this.titleService.setTitle('Edit Testimonial');
                this.updateForm();
                this.loading = false;
            },
            error: () => {
                this.toastr.error('Failed to load testimonial');
                this.loading = false;
            }
        });
    }

    onChangeFile(event: any) {
        const file = event.target.files[0];
        if (file) {
            this.photo = file;
            const reader = new FileReader();
            reader.onload = (e: any) => {
                this.previewUrl = e.target.result;
            };
            reader.readAsDataURL(file);
        }
    }

    save() {
        if (this.form.invalid) return;

        if (!this.photo && !this.testimonialId) {
            this.toastr.error('Please upload a profile photo');
            return;
        }

        this.submitting = true;

        const testimonialData: Testimonial = {
            id: this.testimonialId || '',
            displayName: this.form.value.displayName,
            comment: this.form.value.comment,
            jobTitle: this.form.value.jobTitle,
            company: this.form.value.company,
            location: this.form.value.location,
            profilePhotoPath: '',
            createdAt: ''
        };

        this.testimonialService.save(testimonialData, this.photo).subscribe({
            next: () => {
                this.submitting = false;
                this.toastr.success(this.testimonialId ? 'Testimonial updated successfully' : 'Testimonial added successfully');
                this.router.navigateByUrl('/admin/testimonials');
            },
            error: () => {
                this.submitting = false;
                this.toastr.error('Failed to save testimonial');
            }
        });
    }

    getFullPhotoUrl(path: string): string {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${environment.apiUrl}/${path}`;
    }

    private buildForm() {
        this.form = this.fb.group({
            displayName: new UntypedFormControl('', [Validators.required]),
            comment: new UntypedFormControl('', [Validators.required]),
            jobTitle: new UntypedFormControl('', [Validators.required]),
            company: new UntypedFormControl('', [Validators.required]),
            location: new UntypedFormControl('', [Validators.required]),
            photo: new UntypedFormControl('', this.testimonialId ? [] : [Validators.required])
        });
    }

    private updateForm() {
        this.form.patchValue({
            displayName: this.testimonial?.displayName,
            comment: this.testimonial?.comment,
            jobTitle: this.testimonial?.jobTitle,
            company: this.testimonial?.company,
            location: this.testimonial?.location
        });
        this.form.get('photo')?.clearValidators();
    }
}
